import React, { useState, useEffect, useMemo } from "react";
import {
  User,
  Globe,
  Sparkle,
  Shield,
  Question,
  SignOut,
  CaretRight,
  CaretLeft,
  Coins,
  EnvelopeSimple,
  Lock,
  Heart,
  Image as ImageIcon,
  Check,
  ShieldCheck,
  Gift,
  SpeakerHigh,
  Trash,
} from "@phosphor-icons/react";
import BrandMark from "./BrandMark";
import InfoPage from "./InfoPage";
import VaultPinManager from "./VaultPinManager";
import VoiceSpeedPicker from "./VoiceSpeedPicker";
import VoiceGenderPicker from "./VoiceGenderPicker";
import ClearDataManager from "./ClearDataManager";
import ConnectionDiagnostics from "./ConnectionDiagnostics";
import DeleteAccountFlow from "./DeleteAccountFlow";
import { referralEnabled } from "../../lib/featureFlags";
import { APP_REVIEW_LOCKDOWN } from "../../lib/appReview";
import { shouldSkipDeleteConfirm, setSkipDeleteConfirm } from "../../lib/confirmDelete";
import proCrownImg from "../../assets/pro-crown-orange.png";
import InviteModal from "../InviteModal";
import LoginSheet from "../LoginSheet";
import { useAuth } from "../../lib/auth";
import { getHomeCurrency, setHomeCurrency } from "../../lib/currency";
import { hasPin } from "../../lib/vaultLock";
import {
  WATERMARK_LEVELS,
  getWatermarkLevel,
  setWatermarkLevel,
  getWatermarkLabel,
} from "../../lib/watermarkSettings";
import { isIOS } from "../../lib/platform";
import { getTtsSpeed, getTtsSpeedLabel } from "../../lib/ttsSpeed";
import { getTtsVoice } from "../../lib/ttsVoice";
import {
  ABOUT_PARAGRAPHS,
  ABOUT_FEATURES,
  PRO_UNLOCKS,
  FEEDBACK_BLURB,
  PAYMENTS_OVERVIEW,
  PAYMENTS_PROCESSING,
  PRICING_PLANS,
  TERMS_COPY,
  TERMS_OF_USE_SECTIONS,
  PRIVACY_SECTIONS,
  PRIVACY_RIGHTS_COPY,
  LAST_UPDATED,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
  SUPPORT_FAQ,
} from "../../lib/legalCopy";
import {
  SparkleIcon,
  CoinsIcon,
  GlobeIcon,
  CreditCardIcon,
  ImageIcon as TipImageIcon,
  ShieldCheckIcon,
  SpeakerIcon,
  GiftIcon,
  QuestionIcon,
  ShieldIcon,
} from "./SettingsIcons";
import {
  GlobeOutlineIcon,
  CreditCardOutlineIcon,
  ImageOutlineIcon,
  ShieldCheckOutlineIcon,
  SpeakerOutlineIcon,
  QuestionOutlineIcon,
  InfoOutlineIcon,
  ShieldLockOutlineIcon,
  TrashOutlineIcon,
} from "./SettingsOutlineIcons";

/** Inline PNG-backed "PRO" shield icon — gold + navy gradient. Rendered
 * as an <img> tag, larger than the other Row icons so the premium upsell
 * actually pops. A small negative margin keeps the row baseline tidy. */
function ProShieldIcon() {
  return (
    <img
      src="/assets/pro-shield-icon.png"
      alt=""
      aria-hidden="true"
      width={48}
      height={48}
      style={{ objectFit: "contain", margin: "-10px -4px" }}
    />
  );
}

/** Help & Legal section icons — rendered at 2× the App-section size so
 * the navy + orange detail (question mark, padlock) reads clearly. */
function HelpRowIcon() {
  return (
    <div style={{ margin: "0 -1px" }}>
      <QuestionIcon size={28} />
    </div>
  );
}
function PrivacyRowIcon() {
  return (
    <div style={{ margin: "-6px -2px" }}>
      <ShieldIcon size={44} />
    </div>
  );
}

/** PNG-backed gold coin stack icon for "Buy credits". Larger than the
 * standard row icons so the 3D coin metaphor reads clearly. */
function CoinsImageIcon() {
  return (
    <img
      src="/assets/coins-icon.png"
      alt=""
      aria-hidden="true"
      width={40}
      height={40}
      style={{ objectFit: "contain", margin: "-6px -2px" }}
    />
  );
}

/** PNG-backed 3D gift box icon for "Invite a friend". */
function GiftImageIcon() {
  return (
    <img
      src="/assets/gift-icon.png"
      alt=""
      aria-hidden="true"
      width={40}
      height={40}
      style={{ objectFit: "contain", margin: "-6px -2px" }}
    />
  );
}

/** App-section icons that need to read larger than the default 22px row
 * size (Vault security shield + Voice speed speaker — the user wanted
 * these scaled up so the navy outline + orange accent details pop). */
function VaultSecurityRowIcon() {
  return (
    <div style={{ margin: "-4px -2px" }}>
      <ShieldCheckIcon size={34} />
    </div>
  );
}
function VoiceSpeedRowIcon() {
  return (
    <div style={{ margin: "-4px -2px" }}>
      <SpeakerIcon size={34} />
    </div>
  );
}

/** Lucide-style trash icon for the Clear Data row. Slightly inset so it
 * sits at the same optical weight as the other Section icons. */
function ClearDataIcon({ size = 34 }) {
  return (
    <div style={{ margin: "-4px -2px" }}>
      <Trash size={size - 6} weight="duotone" color="#0E2C54" />
    </div>
  );
}

/**
 * Settings tab — account, subscription, language, privacy, help.
 *
 * Has internal navigation: top-level list ↔ Help / Privacy / Language /
 * Home Currency sub-pages.
 */
export default function SettingsTab({ openPaywall, returnToTranslate }) {
  const { user, signIn, signOut, refresh, isProEffective } = useAuth();
  // T49 — `isPro` now ORs in the locally-cached Apple entitlement so
  // a paid subscriber NEVER sees "Free plan" in Settings during a
  // transient backend outage. Backend remains source of truth for
  // renewals; cache only persists a validated StoreKit transaction.
  const isPro = isProEffective || user?.plan === "pro";
  const isTrial = user?.plan === "trial";
  const [view, setView] = useState("root");
  const [homeCurrencyCode, setHomeCurrencyCode] = useState(getHomeCurrency());
  const [watermarkLevel, setWatermarkLevelState] = useState(getWatermarkLevel());
  const [vaultPinOn, setVaultPinOn] = useState(hasPin());
  const [ttsSpeed, setTtsSpeedState] = useState(getTtsSpeed());
  const [ttsVoice, setTtsVoiceState] = useState(getTtsVoice());
  const [skipDeleteConfirm, setSkipDeleteConfirmState] = useState(shouldSkipDeleteConfirm());

  // Re-read voice speed every time the user returns to the root view so
  // the "Voice speed" row reflects the latest selection without a reload.
  useEffect(() => {
    if (view === "root") {
      setTtsSpeedState(getTtsSpeed());
      setTtsVoiceState(getTtsVoice());
      setSkipDeleteConfirmState(shouldSkipDeleteConfirm());
    }
  }, [view]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  // T49.L — One-shot crown shimmer on first Pro detection per user.
  // Triggers a ~1.1s scale + golden halo animation the first time
  // `isPro` becomes true for a given user_id, then persists a localStorage
  // flag so the animation never replays for that account.
  const [crownShimmer, setCrownShimmer] = useState(false);
  useEffect(() => {
    if (!isPro) return;
    const key = `tiplingo_pro_crown_seen_${user?.user_id || "anon"}`;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(key) === "1") return;
    setCrownShimmer(true);
    const t = setTimeout(() => {
      setCrownShimmer(false);
      try { window.localStorage.setItem(key, "1"); } catch { /* quota: silent */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [isPro, user?.user_id]);

  useEffect(() => {
    setHomeCurrencyCode(getHomeCurrency());
    setWatermarkLevelState(getWatermarkLevel());
    setVaultPinOn(hasPin());
  }, [view]);

  // T49.X (Build 83) — Silent entitlement reconcile whenever the Settings
  // tab mounts for a signed-in iOS user. Belt-and-suspenders for the
  // "yearly purchased but profile still shows Free Plan" bug:
  //   - Purchase path already reconciles at completion.
  //   - App-resume listener in AuthProvider reconciles on foreground.
  //   - This handler covers the case where the user was already Pro on
  //     the Apple ID before this build was installed (fresh install +
  //     restored account) and lands directly on Settings without going
  //     through the paywall or a resume event.
  // `syncAppleEntitlement` is a no-op on non-iOS and on users with no
  // active StoreKit transactions — safe to call unconditionally.
  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { syncAppleEntitlement } = await import("../../lib/iapAutoSync");
        const synced = await syncAppleEntitlement();
        if (!cancelled && synced?.active) {
          await refresh();
        }
      } catch (_e) { /* silent — non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, refresh]);

  if (view === "help") {
    return <HelpCenter onBack={() => setView("root")} />;
  }
  if (view === "about") {
    return <AboutTipLingo onBack={() => setView("root")} />;
  }
  if (view === "payments") {
    return <PaymentsInfo onBack={() => setView("root")} />;
  }
  if (view === "terms") {
    return <TermsBillingCancellation onBack={() => setView("root")} />;
  }
  if (view === "privacy") {
    return <PrivacyPolicy onBack={() => setView("root")} />;
  }
  if (view === "language") {
    return <LanguagePicker onBack={() => setView("root")} />;
  }
  if (view === "watermark") {
    return (
      <WatermarkPicker
        value={watermarkLevel}
        onChange={(lvl) => {
          setWatermarkLevel(lvl);
          setWatermarkLevelState(lvl);
        }}
        onBack={() => setView("root")}
      />
    );
  }
  if (view === "vault_security") {
    return <VaultPinManager onBack={() => setView("root")} />;
  }
  if (view === "voice_speed") {
    return <VoiceSpeedPicker onBack={() => setView("root")} />;
  }
  if (view === "voice_gender") {
    return <VoiceGenderPicker onBack={() => setView("root")} />;
  }
  if (view === "clear_data") {
    return <ClearDataManager onBack={() => setView("root")} />;
  }
  if (view === "delete_account") {
    return (
      <DeleteAccountFlow
        onBack={() => setView("root")}
        onDeleted={async () => {
          // Sign out + clear local user data + return to root.
          try { await signOut(); } catch { /* tolerate */ }
          try {
            localStorage.removeItem("tiplingo_local_entitlement_v1");
            Object.keys(localStorage).forEach((k) => {
              if (k.startsWith("tiplingo_pro_crown_seen_")) {
                localStorage.removeItem(k);
              }
            });
          } catch { /* quota: silent */ }
          refresh();
          setView("root");
        }}
      />
    );
  }
  if (view === "currency") {
    return (
      <CurrencyPicker
        value={homeCurrencyCode}
        onChange={(c) => {
          setHomeCurrency(c);
          setHomeCurrencyCode(c);
        }}
        onBack={() => setView("root")}
      />
    );
  }

  return (
    <div className="px-5 pt-4" data-testid="settings-screen">
      <div className="flex items-center justify-between mb-5">
        <BrandMark />
      </div>

      {returnToTranslate && (
        <button
          type="button"
          onClick={returnToTranslate}
          data-testid="settings-back-to-translate"
          className="inline-flex items-center gap-1.5 mb-4 bg-forest text-white text-sm font-jakarta font-bold rounded-full px-4 py-2 shadow-soft hover:bg-forest-hover active:scale-[0.98] transition-all"
        >
          <CaretLeft size={16} weight="bold" />
          Back to Translate
        </button>
      )}
      <h1 className="font-jakarta font-extrabold text-4xl text-navy mb-5 leading-none">
        Settings
      </h1>

      {/* Account card */}
      <div
        data-testid="account-card"
        className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 mb-4 flex items-center gap-4"
      >
        <div className="shrink-0 w-14 h-14 rounded-full bg-navy text-white flex items-center justify-center font-jakarta font-extrabold text-xl">
          {user?.email ? (
            user.email[0].toUpperCase()
          ) : (
            <User size={28} weight="duotone" color="#fff" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {user ? (
            <>
              <div className="text-navy font-jakarta font-bold text-base truncate">
                {user.email}
              </div>
              <div
                className="text-navy-soft text-sm font-jakarta font-semibold flex items-center gap-1.5"
                data-testid="account-plan-label"
              >
                {isPro && (
                  <>
                    <img
                      src={proCrownImg}
                      alt=""
                      aria-hidden="true"
                      className={`w-5 h-5 rounded-full object-cover shrink-0 ${crownShimmer ? "animate-crown-shimmer" : ""}`}
                      data-testid="pro-crown-icon"
                    />
                    <span>Pro member</span>
                  </>
                )}
                {isTrial && "🎁 Free trial active"}
                {!isPro && !isTrial && "Free plan"}
              </div>
            </>
          ) : (
            <>
              <div className="text-navy font-jakarta font-bold text-base">
                Not signed in
              </div>
              <div className="text-navy-soft text-sm font-jakarta font-medium">
                Sign in to sync tips across devices.
              </div>
            </>
          )}
        </div>
        {!user && (
          <button
            type="button"
            onClick={() => setLoginOpen(true)}
            data-testid="settings-signin-btn"
            className="shrink-0 bg-orange text-white text-sm font-jakarta font-bold rounded-full px-4 py-2"
          >
            Sign in
          </button>
        )}
      </div>
      <LoginSheet open={loginOpen} onClose={() => setLoginOpen(false)} />

      {/* About TipLingo — added for Stripe-readiness so reviewers can
          see the product description, feature list, and what is being
          sold without needing to scroll the marketing site. */}
      <Section title="About">
        <Row
          Icon={InfoOutlineIcon}
          iconSize={26}
          label="About TipLingo"
          subtitle="What the app does and who it’s for"
          onClick={() => setView("about")}
          testId="settings-about-row"
        />
      </Section>

      {/* Subscription */}
      <Section title="Subscription">
        <Row
          Icon={ProShieldIcon}
          label={isPro ? "Manage subscription" : "Upgrade to Pro"}
          subtitle={
            isPro
              ? (isIOS()
                  ? "Cancel or change plan in Apple Subscriptions"
                  : "Cancel or change plan in Settings")
              : "Expanded translations, scans, and Pro travel tools"
          }
          onClick={async () => {
            if (isPro) {
              // T49.E — Pro user → iOS native subscription
              // management URL on iOS; Stripe portal on web. Apple
              // requires the in-app "Manage subscription" link for
              // IAP subscriptions to route to Apple's flow.
              try {
                const { manageSubscription } = await import("../../lib/manageSubscription");
                await manageSubscription();
              } catch (e) {
                console.warn("[manageSubscription] failed:", e?.message || e);
              }
              return;
            }
            openPaywall?.();
          }}
          testId="settings-upgrade-btn"
        />
        {/* Credit packs are a web-only payment surface. Hidden on iOS so the
            app conforms to Apple's IAP-only rule for digital goods.
            App Review lockdown (Build 83+): also hidden on web so Apple
            reviewers checking the web experience for consistency don't
            find a "Buy credits" surface that isn't in the iOS app.
            `!APP_REVIEW_LOCKDOWN` is a compile-time false — Terser DCEs
            the entire JSX subtree, dropping the "Buy credits" literal
            from the production bundle. Restored automatically when
            APP_REVIEW_LOCKDOWN flips back to false post-approval. */}
        {!APP_REVIEW_LOCKDOWN && !isIOS() && (
          <Row
            Icon={CoinsImageIcon}
            label="Buy credits"
            subtitle="One-time packs for occasional travel"
            onClick={() => openPaywall?.()}
            testId="settings-credits-btn"
          />
        )}
      </Section>

      {/* App */}
      <Section title="App">
        <Row
          Icon={GlobeOutlineIcon}
          iconSize={26}
          label="Language"
          value="English"
          onClick={() => setView("language")}
          testId="settings-language-row"
        />
        <Row
          Icon={CreditCardOutlineIcon}
          iconSize={26}
          label="Home currency"
          value={homeCurrencyCode}
          onClick={() => setView("currency")}
          testId="settings-currency-row"
        />
        <Row
          Icon={ImageOutlineIcon}
          iconSize={26}
          label="Tip-log watermarks"
          value={getWatermarkLabel(watermarkLevel)}
          onClick={() => setView("watermark")}
          testId="settings-watermark-row"
        />
        <Row
          Icon={ShieldCheckOutlineIcon}
          iconSize={26}
          label="Vault security"
          value={vaultPinOn ? "PIN on" : "Off"}
          onClick={() => setView("vault_security")}
          testId="settings-vault-security-row"
        />
        <Row
          Icon={SpeakerOutlineIcon}
          iconSize={26}
          label="Voice speed"
          value={getTtsSpeedLabel(ttsSpeed)}
          onClick={() => setView("voice_speed")}
          testId="settings-voice-speed-row"
        />
        <Row
          Icon={SpeakerOutlineIcon}
          iconSize={26}
          label="Voice"
          value={ttsVoice === "male" ? "Male" : "Female"}
          onClick={() => setView("voice_gender")}
          testId="settings-voice-gender-row"
        />
      </Section>

      {/* Invite a friend — T49.R (Build 79): hardcoded App Review
          lockdown short-circuits the entire referral surface, on TOP
          of the env-driven `referralEnabled()` gate. The
          `!APP_REVIEW_LOCKDOWN` term is a compile-time `false` in
          this build, so webpack DCE strips the JSX block entirely —
          no row, no subtitle, no setInviteOpen call site, no Refer
          a friend Section title. Flip APP_REVIEW_LOCKDOWN to false
          in /app/frontend/src/lib/appReview.js for Build 80+ to
          restore. */}
      {!APP_REVIEW_LOCKDOWN && user && referralEnabled() && (
        <Section title="Refer a friend">
          <Row
            Icon={GiftImageIcon}
            label="Invite a friend"
            subtitle="You both get free Pro days when they join."
            onClick={() => setInviteOpen(true)}
            testId="settings-invite-row"
          />
        </Section>
      )}

      {/* Help & legal */}
      <Section title="Help & legal">
        <Row
          Icon={QuestionOutlineIcon}
          iconSize={26}
          label="Help center"
          onClick={() => setView("help")}
          testId="settings-help-row"
        />
        <Row
          Icon={CreditCardOutlineIcon}
          iconSize={26}
          label="Payments & Subscriptions"
          subtitle="Plans, billing & secure payment processing"
          onClick={() => setView("payments")}
          testId="settings-payments-row"
        />
        <Row
          Icon={InfoOutlineIcon}
          iconSize={26}
          label="Terms of Use"
          subtitle="Liability, disclaimers, vault, AI, billing & arbitration"
          onClick={() => setView("terms")}
          testId="settings-terms-row"
        />
        <Row
          Icon={ShieldLockOutlineIcon}
          iconSize={26}
          label="Privacy policy"
          onClick={() => setView("privacy")}
          testId="settings-privacy-row"
        />
        <Row
          Icon={TrashOutlineIcon}
          iconSize={26}
          label="Clear data"
          subtitle="Wipe history, favorites, and saved items on this device."
          onClick={() => setView("clear_data")}
          testId="settings-clear-data-row"
        />
        {skipDeleteConfirm && (
          <Row
            Icon={ShieldCheckOutlineIcon}
            iconSize={26}
            label="Re-enable delete confirmations"
            subtitle="Show 'Are you sure?' before any item is deleted."
            onClick={() => {
              setSkipDeleteConfirm(false);
              setSkipDeleteConfirmState(false);
            }}
            testId="settings-reenable-delete-confirm-row"
          />
        )}
      </Section>

      {/* Sign out */}
      {user && (
        <button
          type="button"
          onClick={async () => {
            await signOut();
            refresh();
          }}
          data-testid="settings-signout-btn"
          className="w-full mt-6 bg-white border border-navy/10 rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 text-orange font-jakarta font-bold"
        >
          <SignOut size={20} weight="duotone" /> Sign out
        </button>
      )}

      {/* T49.T (Build 81) — Apple 5.1.1(v) in-app account deletion.
          Only surfaced when a user is signed in. Opens DeleteAccountFlow
          which renders a warning screen first, then a final confirm,
          then a success screen, then signs the user out and clears
          local data. */}
      {user && (
        <button
          type="button"
          onClick={() => setView("delete_account")}
          data-testid="settings-delete-account-btn"
          className="w-full mt-3 bg-white border border-navy/10 rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 text-navy-soft font-jakarta font-semibold"
        >
          Delete account
        </button>
      )}

      <div className="text-center text-navy-soft text-xs mt-6 pb-4 font-jakarta">
        TipLingo · Your travel companion
      </div>

      <InviteModal open={!APP_REVIEW_LOCKDOWN && inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="text-navy-soft text-xs uppercase tracking-wider font-jakarta font-bold mb-2 px-1">
        {title}
      </div>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function Row({ Icon, label, subtitle, value, onClick, testId, iconSize = 22 }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-bone/60 transition-colors border-b border-navy/5 last:border-b-0"
    >
      {/* Pass full styling props so duotone Phosphor icons keep their look,
          while custom dual-color SVG icons (SettingsIcons) simply ignore
          the unknown props. Either component renders correctly. */}
      <Icon size={iconSize} weight="duotone" color="#0E2C54" />
      <div className="flex-1 min-w-0">
        <div className="text-navy font-jakarta font-bold text-base leading-tight">
          {label}
        </div>
        {subtitle && (
          <div className="text-navy-soft text-sm mt-0.5 font-jakarta font-medium">
            {subtitle}
          </div>
        )}
      </div>
      {value && (
        <div className="text-navy-soft text-sm font-jakarta font-semibold">
          {value}
        </div>
      )}
      {onClick && <CaretRight size={18} weight="bold" color="#4A5D78" />}
    </Comp>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-pages                                                          */
/* ------------------------------------------------------------------ */

function HelpCenter({ onBack }) {
  const [q, setQ] = useState("");
  // Case-insensitive haystack across question + answer. Two-pass match:
  //   1. Exact full-phrase substring match (preserves the original behaviour
  //      for single keywords like "refund" or "cancel" — fastest, most
  //      precise).
  //   2. Natural-language fallback — tokenize the query on whitespace, drop
  //      filler words shorter than 4 characters ("how", "the", "is", "are",
  //      "and", etc.), and match any FAQ whose question or answer contains
  //      any of the meaningful tokens. This is what makes a search like
  //      "How much is the monthly subscription" land on the pricing /
  //      cancellation FAQs instead of returning empty.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return SUPPORT_FAQ;
    const exact = SUPPORT_FAQ.filter(
      (item) =>
        item.q.toLowerCase().includes(needle) ||
        item.a.toLowerCase().includes(needle),
    );
    if (exact.length > 0) return exact;
    const tokens = needle.split(/\s+/).filter((t) => t.length >= 4);
    if (tokens.length === 0) return [];
    return SUPPORT_FAQ.filter((item) => {
      const hay = (item.q + " " + item.a).toLowerCase();
      return tokens.some((t) => hay.includes(t));
    });
  }, [q]);
  return (
    <InfoPage title="Help Center" onBack={onBack}>
      {/* Hero card mirroring the public /support page — primary email CTA
          is the first thing users see. */}
      <div className="bg-navy text-white rounded-2xl shadow-deep p-5 sm:p-6">
        <h3 className="font-jakarta font-extrabold text-xl mb-1.5">
          Need help? Email us.
        </h3>
        <p className="text-white/80 text-sm font-jakarta font-medium mb-4 leading-relaxed">
          Every email is read by a real person. We usually reply within one
          business day.
        </p>
        <a
          href={SUPPORT_MAILTO}
          data-testid="help-email-cta"
          className="inline-flex items-center justify-center rounded-full bg-orange hover:bg-orange-hover text-white font-jakarta font-extrabold text-sm px-5 py-3"
        >
          <EnvelopeSimple
            size={16}
            weight="fill"
            className="inline mr-2 -mt-0.5"
          />
          Email {SUPPORT_EMAIL}
        </a>
        <p className="text-white/60 text-[11px] font-jakarta mt-3">
          Please include your device (iPhone model + iOS version) and a brief
          description of the issue. Screenshots help us debug faster.
        </p>
      </div>

      {/* FAQ — shared `SUPPORT_FAQ` with the public /support page so the
          two surfaces never drift apart. */}
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5">
        <h3 className="font-jakarta font-extrabold text-lg text-navy mb-3">
          Frequently asked questions
        </h3>
        <div className="relative mb-4">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search — try “cancel”, “refund”, “sign in”…"
            data-testid="help-faq-search"
            className="w-full bg-bone border border-navy/10 rounded-full pl-10 pr-4 py-2.5 text-[13px] text-navy font-jakarta placeholder:text-navy-soft focus:outline-none focus:ring-2 focus:ring-orange/40 focus:border-orange/40"
          />
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-soft"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
        </div>
        {filtered.length === 0 ? (
          <div
            data-testid="help-faq-empty"
            className="text-navy-soft text-[13px] py-2"
          >
            No FAQ matches “{q}”. Try a different keyword, or{" "}
            <a
              href={SUPPORT_MAILTO}
              className="text-orange font-bold hover:underline"
            >
              email {SUPPORT_EMAIL}
            </a>
            .
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((item) => {
              const idx = SUPPORT_FAQ.indexOf(item);
              return (
                <div
                  key={idx}
                  data-testid={`help-faq-item-${idx}`}
                  className="border-b border-navy/5 pb-4 last:border-0 last:pb-0"
                >
                  <div className="font-jakarta font-extrabold text-navy text-sm mb-1">
                    {item.q}
                  </div>
                  <div className="text-navy/80 text-[13px] leading-relaxed font-jakarta">
                    {item.a}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Manage your subscription — Apple specifically requires users to be
          able to find how to cancel. */}
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5">
        <h3 className="font-jakarta font-extrabold text-lg text-navy mb-3">
          Manage your subscription
        </h3>
        <ul className="space-y-2.5 text-navy text-[13px] font-jakarta">
          <li className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <span>
              <strong className="font-jakarta font-bold">iOS:</strong>{" "}
              Settings app → your Apple ID name → Subscriptions → TipLingo →
              Cancel Subscription.
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <span>
              <strong className="font-jakarta font-bold">Web:</strong>{" "}
              Account → Manage subscription (opens the customer portal).
              Cancel, change card, or download invoices.
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <span>
              <strong className="font-jakarta font-bold">Refunds:</strong>{" "}
              iOS purchases —{" "}
              <a
                href="https://reportaproblem.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange font-bold hover:underline"
              >
                reportaproblem.apple.com
              </a>
              . Web purchases — email{" "}
              <a
                href={SUPPORT_MAILTO}
                className="text-orange font-bold hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </span>
          </li>
        </ul>
      </div>

      {/* Footer fallback CTA in case the user scrolled past the hero. */}
      <a
        href={`${SUPPORT_MAILTO}?subject=TipLingo%20support`}
        className="block bg-orange text-white font-jakarta font-extrabold text-base text-center rounded-full py-3.5 shadow-soft"
        data-testid="help-email-btn"
      >
        <EnvelopeSimple size={18} weight="fill" className="inline mr-2 -mt-0.5" />
        Email support
      </a>
    </InfoPage>
  );
}

function PrivacyPolicy({ onBack }) {
  return (
    <InfoPage title="Privacy Policy" onBack={onBack}>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 space-y-4 text-navy text-sm font-jakarta font-medium leading-relaxed">
        <p className="text-navy-soft text-xs uppercase tracking-widest font-jakarta font-bold">
          Last updated: {LAST_UPDATED}
        </p>

        {PRIVACY_SECTIONS.map((section) => (
          <React.Fragment key={section.heading}>
            <h3 className="font-jakarta font-extrabold text-base text-navy mt-3">
              {section.heading}
            </h3>
            {section.heading === "Your rights" ? (
              <p>
                {PRIVACY_RIGHTS_COPY.split(SUPPORT_EMAIL)[0]}
                <a
                  className="text-orange font-bold hover:underline"
                  href={SUPPORT_MAILTO}
                  data-testid="privacy-support-mailto"
                >
                  {SUPPORT_EMAIL}
                </a>
                {PRIVACY_RIGHTS_COPY.split(SUPPORT_EMAIL)[1]}
              </p>
            ) : (
              <p>{section.body}</p>
            )}
          </React.Fragment>
        ))}
      </div>
    </InfoPage>
  );
}

/**
 * About TipLingo sub-page — mirrors the public /about route. Stripe and
 * Apple review teams expect the in-app description to match what we
 * publish marketing-side, so both pull from the same legalCopy module.
 *
 * T49.N (Build 74) — Connection Diagnostics is no longer surfaced as
 * a visible row to normal users (App-Review polish: avoid exposing
 * raw backend probes / route names in the consumer UI). Instead it is
 * gated behind a 5-tap easter egg on the "About TipLingo" title. App
 * Review notes call out this exact gesture so the reviewer can still
 * reach it on demand.
 */
function AboutTipLingo({ onBack }) {
  const [diagOpen, setDiagOpen] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const tapResetRef = React.useRef(null);
  const UNLOCK_TAPS = 5;
  const TAP_WINDOW_MS = 2500;

  function handleTitleTap() {
    if (tapResetRef.current) clearTimeout(tapResetRef.current);
    setTapCount((prev) => {
      const next = prev + 1;
      if (next >= UNLOCK_TAPS) {
        setDiagOpen(true);
        return 0;
      }
      return next;
    });
    tapResetRef.current = setTimeout(() => setTapCount(0), TAP_WINDOW_MS);
  }

  React.useEffect(() => {
    return () => {
      if (tapResetRef.current) clearTimeout(tapResetRef.current);
    };
  }, []);

  if (diagOpen) {
    return <ConnectionDiagnostics onBack={() => setDiagOpen(false)} />;
  }
  return (
    <InfoPage title="About TipLingo" onBack={onBack} onTitleTap={handleTitleTap}>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 text-navy text-sm font-jakarta font-medium leading-relaxed space-y-3">
        {ABOUT_PARAGRAPHS.map((p, i) => (
          <p key={i} data-testid={`about-paragraph-${i}`}>
            {p}
          </p>
        ))}
      </div>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5">
        <h3 className="font-jakarta font-extrabold text-base text-navy mb-2">
          TipLingo includes
        </h3>
        <ul className="space-y-2 text-navy text-sm font-jakarta font-medium">
          {ABOUT_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2.5">
              <span
                aria-hidden
                className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </InfoPage>
  );
}

/**
 * Payments & Subscriptions sub-page. Spelled out for Stripe review so
 * users can find pricing, billing cadence, and payment-processor
 * disclosures without leaving the app.
 */
function PaymentsInfo({ onBack }) {
  return (
    <InfoPage title="Payments & Subscriptions" onBack={onBack}>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 text-navy text-sm font-jakarta font-medium leading-relaxed space-y-3">
        <p>{PAYMENTS_OVERVIEW}</p>
      </div>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5">
        <h3 className="font-jakarta font-extrabold text-base text-navy mb-3">
          Current plans
        </h3>
        <ul className="divide-y divide-navy/10">
          {PRICING_PLANS.map((p) => (
            <li
              key={p.label}
              className="py-3 flex items-baseline justify-between gap-3"
              data-testid={`pricing-row-${p.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "")}`}
            >
              <span className="font-jakarta font-bold text-navy text-sm flex-1 min-w-0">
                {p.label}
              </span>
              <span className="font-jakarta text-navy-soft text-xs text-right">
                {p.price}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 text-navy text-sm font-jakarta font-medium leading-relaxed">
        <p>{PAYMENTS_PROCESSING}</p>
      </div>
    </InfoPage>
  );
}

/**
 * Terms of Use sub-page. Mirrors the public /terms route verbatim so a
 * reviewer (Apple, Stripe) or user sees the same legal language whether
 * they open the URL or browse via Settings. Renders all 23 industry-grade
 * Terms sections (acceptance / eligibility / accuracy disclaimers / vault
 * disclaimer / AI disclaimer / limitation of liability / indemnification /
 * arbitration + class-action waiver / changes / contact) followed by the
 * subscription appendix with cancellation copy.
 */
function TermsBillingCancellation({ onBack }) {
  const [pre, post] = TERMS_COPY.split(SUPPORT_EMAIL);
  return (
    <InfoPage title="Terms of Use" onBack={onBack}>
      <p className="text-navy-soft text-xs uppercase tracking-widest font-jakarta font-bold">
        Last updated: {LAST_UPDATED}
      </p>

      {TERMS_OF_USE_SECTIONS.map((section) => (
        <div
          key={section.id}
          data-testid={`settings-terms-section-${section.id}`}
          className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 text-navy text-sm font-jakarta font-medium leading-relaxed"
        >
          <h3 className="font-jakarta font-extrabold text-navy text-base mb-2">
            {section.heading}
          </h3>
          <p className="whitespace-pre-line">{section.body}</p>
        </div>
      ))}

      <div
        data-testid="settings-terms-cancellation"
        className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 text-navy text-sm font-jakarta font-medium leading-relaxed"
      >
        <h3 className="font-jakarta font-extrabold text-navy text-base mb-2">
          Appendix — Cancellation &amp; Refunds
        </h3>
        <p>
          {pre}
          <a
            className="text-orange font-bold hover:underline"
            href={SUPPORT_MAILTO}
            data-testid="terms-support-mailto"
          >
            {SUPPORT_EMAIL}
          </a>
          {post}
        </p>
      </div>
    </InfoPage>
  );
}

function LanguagePicker({ onBack }) {
  // Phase 4 — full localization. For now we surface the placeholder.
  return (
    <InfoPage title="Language" onBack={onBack}>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 text-center">
        <Globe size={40} weight="duotone" color="#0E2C54" className="mx-auto" />
        <div className="font-jakarta font-extrabold text-navy text-lg mt-2">
          English
        </div>
        <div className="text-navy-soft text-sm font-jakarta font-medium mt-1">
          Additional UI languages (Spanish, French, Portuguese, German, Japanese)
          are coming in the next update.
        </div>
      </div>
      <div className="bg-orange-soft rounded-2xl border border-orange/15 p-4 text-navy-soft text-sm font-jakarta font-medium">
        <b className="text-navy font-jakarta font-bold">Note:</b> This only
        changes the app's UI language. The Translate feature already supports
        30+ languages independently.
      </div>
    </InfoPage>
  );
}

function WatermarkPicker({ value, onChange, onBack }) {
  const DESCRIPTIONS = {
    off: "Clean cards — no watermark behind your saved tips.",
    subtle: "Barely visible — a hint of the landmark, easy to ignore.",
    medium: "Default — clearly visible but never distracts from the data.",
    bold: "Bold — landmark reads like a postcard backdrop.",
  };
  const OPACITY = { off: 0, subtle: 0.06, medium: 0.10, bold: 0.14 };

  return (
    <div className="px-5 pt-4 pb-28" data-testid="watermark-picker">
      <div className="flex items-center mb-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="watermark-back"
          aria-label="Back"
          className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center mr-3"
        >
          <CaretLeft size={20} weight="bold" color="#0E2C54" />
        </button>
        <h2 className="font-jakarta font-extrabold text-navy text-xl">
          Tip-log watermarks
        </h2>
      </div>
      <p className="text-navy-soft text-sm font-jakarta font-medium mb-4 leading-relaxed">
        Show the country's landmark as a soft backdrop on each saved-tip card.
        Statue of Liberty for NYC, Eiffel Tower for Paris, Mt. Fuji for Tokyo —
        pick how loud you want it.
      </p>

      <div className="space-y-3">
        {WATERMARK_LEVELS.map((lvl) => {
          const active = lvl === value;
          return (
            <button
              key={lvl}
              type="button"
              data-testid={`watermark-option-${lvl}`}
              onClick={() => onChange(lvl)}
              className={`w-full rounded-2xl border bg-white p-4 flex items-center gap-3 text-left relative overflow-hidden transition-all ${
                active
                  ? "border-orange shadow-soft ring-2 ring-orange/30"
                  : "border-navy/10 shadow-sm"
              }`}
            >
              {OPACITY[lvl] > 0 && (
                <div
                  className="absolute inset-y-0 right-0 w-1/2 pointer-events-none flex items-center justify-end"
                  aria-hidden
                >
                  <img
                    src="/assets/landmarks/US.jpg"
                    alt=""
                    className="h-[140%] w-auto object-contain"
                    style={{ opacity: OPACITY[lvl] }}
                  />
                </div>
              )}
              <div
                className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center relative z-10 ${
                  active ? "bg-orange text-white" : "bg-bone/80 text-navy"
                }`}
              >
                {active ? (
                  <Check size={20} weight="bold" />
                ) : (
                  <ImageIcon size={20} weight="duotone" />
                )}
              </div>
              <div className="flex-1 min-w-0 relative z-10">
                <div className="font-jakarta font-extrabold text-navy text-base capitalize">
                  {getWatermarkLabel(lvl)}
                </div>
                <div className="text-navy-soft text-xs font-jakarta font-medium mt-0.5 leading-snug">
                  {DESCRIPTIONS[lvl]}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-center text-navy-soft text-xs font-jakarta font-medium mt-5">
        Setting saved automatically. Open the Saved tab to see it live.
      </p>
    </div>
  );
}


function CurrencyPicker({ value, onChange, onBack }) {
  const OPTIONS = [
    { code: "USD", flag: "🇺🇸", name: "US Dollar" },
    { code: "EUR", flag: "🇪🇺", name: "Euro" },
    { code: "GBP", flag: "🇬🇧", name: "British Pound" },
    { code: "CAD", flag: "🇨🇦", name: "Canadian Dollar" },
    { code: "AUD", flag: "🇦🇺", name: "Australian Dollar" },
    { code: "NZD", flag: "🇳🇿", name: "New Zealand Dollar" },
    { code: "JPY", flag: "🇯🇵", name: "Japanese Yen" },
    { code: "KRW", flag: "🇰🇷", name: "South Korean Won" },
    { code: "CNY", flag: "🇨🇳", name: "Chinese Yuan" },
    { code: "HKD", flag: "🇭🇰", name: "Hong Kong Dollar" },
    { code: "TWD", flag: "🇹🇼", name: "Taiwan Dollar" },
    { code: "SGD", flag: "🇸🇬", name: "Singapore Dollar" },
    { code: "INR", flag: "🇮🇳", name: "Indian Rupee" },
    { code: "THB", flag: "🇹🇭", name: "Thai Baht" },
    { code: "VND", flag: "🇻🇳", name: "Vietnamese Dong" },
    { code: "IDR", flag: "🇮🇩", name: "Indonesian Rupiah" },
    { code: "PHP", flag: "🇵🇭", name: "Philippine Peso" },
    { code: "MYR", flag: "🇲🇾", name: "Malaysian Ringgit" },
    { code: "MXN", flag: "🇲🇽", name: "Mexican Peso" },
    { code: "BRL", flag: "🇧🇷", name: "Brazilian Real" },
    { code: "ARS", flag: "🇦🇷", name: "Argentine Peso" },
    { code: "CLP", flag: "🇨🇱", name: "Chilean Peso" },
    { code: "COP", flag: "🇨🇴", name: "Colombian Peso" },
    { code: "PEN", flag: "🇵🇪", name: "Peruvian Sol" },
    { code: "CHF", flag: "🇨🇭", name: "Swiss Franc" },
    { code: "SEK", flag: "🇸🇪", name: "Swedish Krona" },
    { code: "NOK", flag: "🇳🇴", name: "Norwegian Krone" },
    { code: "DKK", flag: "🇩🇰", name: "Danish Krone" },
    { code: "ISK", flag: "🇮🇸", name: "Icelandic Króna" },
    { code: "PLN", flag: "🇵🇱", name: "Polish Złoty" },
    { code: "CZK", flag: "🇨🇿", name: "Czech Koruna" },
    { code: "HUF", flag: "🇭🇺", name: "Hungarian Forint" },
    { code: "RON", flag: "🇷🇴", name: "Romanian Leu" },
    { code: "TRY", flag: "🇹🇷", name: "Turkish Lira" },
    { code: "RUB", flag: "🇷🇺", name: "Russian Ruble" },
    { code: "UAH", flag: "🇺🇦", name: "Ukrainian Hryvnia" },
    { code: "AED", flag: "🇦🇪", name: "UAE Dirham" },
    { code: "SAR", flag: "🇸🇦", name: "Saudi Riyal" },
    { code: "QAR", flag: "🇶🇦", name: "Qatari Riyal" },
    { code: "KWD", flag: "🇰🇼", name: "Kuwaiti Dinar" },
    { code: "ILS", flag: "🇮🇱", name: "Israeli Shekel" },
    { code: "EGP", flag: "🇪🇬", name: "Egyptian Pound" },
    { code: "ZAR", flag: "🇿🇦", name: "South African Rand" },
    { code: "NGN", flag: "🇳🇬", name: "Nigerian Naira" },
    { code: "KES", flag: "🇰🇪", name: "Kenyan Shilling" },
    { code: "MAD", flag: "🇲🇦", name: "Moroccan Dirham" },
  ];
  return (
    <InfoPage title="Home Currency" onBack={onBack}>
      <p className="text-navy-soft text-sm font-jakarta font-medium px-1">
        Your home currency is used to convert tips and totals back so you always
        know what you're paying.
      </p>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 overflow-hidden">
        {OPTIONS.map((o, i) => {
          const active = o.code === value;
          return (
            <button
              key={o.code}
              data-testid={`home-currency-${o.code}`}
              onClick={() => onChange(o.code)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                i < OPTIONS.length - 1 ? "border-b border-navy/5" : ""
              } ${active ? "bg-orange-soft" : "hover:bg-bone/60"}`}
            >
              <span className="text-2xl leading-none">{o.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="text-navy font-jakarta font-bold text-base">
                  {o.code}
                </div>
                <div className="text-navy-soft text-sm font-jakarta font-medium">
                  {o.name}
                </div>
              </div>
              {active && (
                <div className="w-6 h-6 rounded-full bg-orange text-white text-xs font-jakarta font-extrabold flex items-center justify-center">
                  ✓
                </div>
              )}
            </button>
          );
        })}
      </div>
    </InfoPage>
  );
}

function Article({ Icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 flex items-start gap-3">
      <div className="shrink-0 w-10 h-10 rounded-full bg-orange-soft flex items-center justify-center">
        <Icon size={20} weight="duotone" color="#E55B2C" />
      </div>
      <div className="flex-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          {title}
        </div>
        <div className="text-navy-soft text-sm mt-1 font-jakarta font-medium leading-relaxed">
          {body}
        </div>
      </div>
    </div>
  );
}

// silence unused-import warnings (Lock kept for future)
void Lock;
